import { useEffect, useState } from "react";

import { Close, Pause, PlayArrow, Settings } from "@material-ui/icons";
import { Alert, Button, Form, Input, Popover, Select, Slider } from "antd";
import { addMonths } from "date-fns";
import { Map } from "mapbox-gl";
import styled from "styled-components";

import { useRadiusConverter } from "hooks";

import { ProductKey, useTimelineAdapter } from "./hooks/adapters/useTimelineAdapter";

// ── Product options (UI-only) ────────────────────────────────────────────────

const Products = [
  { label: "BOE", value: "BOE" as ProductKey },
  { label: "Oil (Mbbl)", value: "Oil" as ProductKey },
  { label: "Gas (MMcf)", value: "Gas" as ProductKey }
];

// ── Component props ──────────────────────────────────────────────────────────

export interface TimelineModel {
  map: Map;
  onClose: () => void;
}

// ── Timeline component ───────────────────────────────────────────────────────

export default function Timeline({ map, onClose }: TimelineModel) {
  const {
    currentIndex,
    maxValue,
    maxRadius,
    product,
    model,
    mode,
    isInitializing,
    isError,
    play,
    pause,
    setCurrentIndex,
    setMaxValue,
    setMaxRadius,
    setProduct,
    close
  } = useTimelineAdapter(map, onClose);

  function tipFormatter(index: number) {
    if (!model) {
      return <div></div>;
    }
    const date = new Date(model.minTime);
    const newDate = addMonths(date, index);
    return <div>{newDate.toLocaleDateString()}</div>;
  }

  if (!map) {
    return <div>Loading...</div>;
  }

  return (
    <RootContainer>
      <ControlContainer>
        <ToolbarContainer>
          <ButtonIconCentered
            onClick={() => close()}
            ghost
            type="text"
            icon={<Close></Close>}></ButtonIconCentered>
        </ToolbarContainer>
        <PlayAndSlider>
          <Popover
            content={
              <PopoverContent
                product={product}
                maxRadius={maxRadius}
                onChange={(settingsModel: PopoverContentModel) => {
                  setMaxRadius(settingsModel.maxRadius);
                  setMaxValue(settingsModel.maxValue);
                  setProduct(settingsModel.product);

                  if (settingsModel.product !== product) {
                    setTimeout(() => {
                      pause();
                      play(settingsModel.product);
                    }, 100);
                  }
                }}
                maxValue={maxValue}></PopoverContent>
            }>
            <ButtonIconCentered
              shape="circle"
              ghost
              dashed
              type="primary"
              icon={<Settings fontSize="large"></Settings>}></ButtonIconCentered>
          </Popover>
          {mode === "Play" && (
            <ButtonIconCentered
              type="primary"
              onClick={pause}
              icon={<Pause fontSize="large"></Pause>}
              shape="circle"></ButtonIconCentered>
          )}
          {mode === "Paused" && (
            <ButtonIconCentered
              type="primary"
              onClick={() => play(product)}
              loading={isInitializing}
              icon={<PlayArrow fontSize="large"></PlayArrow>}
              shape="circle"></ButtonIconCentered>
          )}
          {!isError && (
            <SliderWrapper>
              <Slider
                max={model?.maxIndex}
                value={currentIndex}
                onChange={setCurrentIndex}
                tipFormatter={tipFormatter}></Slider>
            </SliderWrapper>
          )}
          {isError && (
            <Alert type="error" message="Unable to connect to backend services."></Alert>
          )}
        </PlayAndSlider>
      </ControlContainer>
    </RootContainer>
  );
}

// ── PopoverContent (settings form) ───────────────────────────────────────────

interface PopoverContentModel {
  product: ProductKey;
  maxRadius: number;
  maxValue: number;
  // eslint-disable-next-line no-unused-vars
  onChange?: (model: PopoverContentModel) => void;
}

const PopoverContent = ({
  product,
  maxValue,
  maxRadius,
  onChange
}: PopoverContentModel) => {
  const { displayLabel, metersToDisplay, displayToMeters, isReady } =
    useRadiusConverter();

  const [productKey, setProductKey] = useState<ProductKey>(product);
  const [maxValueLabel, setMaxValueLabel] = useState("");
  const [value, setMaxValue] = useState(maxValue);
  const [radiusInMeters, setRadiusInMeters] = useState(maxRadius);
  const [displayedRadius, setDisplayedRadius] = useState<string>(maxRadius.toString());
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (isEditing) return; // Don't update while user is typing

    if (maxRadius && isReady) {
      const convertedValue = metersToDisplay(maxRadius);
      // Round to 2 decimal places for cleaner display
      setDisplayedRadius((Math.round(convertedValue * 100) / 100).toString());
    } else if (maxRadius) {
      setDisplayedRadius((Math.round(maxRadius * 100) / 100).toString());
    }
    setRadiusInMeters(maxRadius);
  }, [maxRadius, isReady, metersToDisplay, isEditing]);

  useEffect(() => {
    const label = Products.filter((p) => p.value === productKey)[0]?.label;
    setMaxValueLabel("Max " + label);
  }, [productKey]);

  return (
    <div>
      <Form layout="vertical">
        <h5>Settings</h5>
        <Form.Item label="Product">
          <Select
            options={Products}
            value={productKey}
            popupClassName="modal-select"
            onChange={(key) => {
              setProductKey(key);
              onChange &&
                onChange({
                  maxValue: value,
                  product: key,
                  maxRadius: radiusInMeters
                });
            }}></Select>
        </Form.Item>
        <Form.Item label={maxValueLabel}>
          <Input
            value={value}
            type="number"
            min={1}
            onChange={(evt) => setMaxValue(parseFloat(evt.target.value))}
            onBlur={() =>
              onChange &&
              onChange({
                maxValue: value,
                product: productKey,
                maxRadius: radiusInMeters
              })
            }></Input>
        </Form.Item>
        <Form.Item label={`Max Radius (${displayLabel})`}>
          <Input
            type="number"
            value={displayedRadius}
            min={100}
            onFocus={() => setIsEditing(true)}
            onChange={(evt) => {
              const inputValue = evt.target.value;
              setDisplayedRadius(inputValue);
            }}
            onBlur={(evt) => {
              setIsEditing(false);
              // Convert displayed value back to meters for backend
              const inputValue = evt.target.value;
              const numericValue = parseFloat(inputValue);
              if (!isNaN(numericValue)) {
                const metersValue = displayToMeters(numericValue);
                setRadiusInMeters(metersValue);
                onChange &&
                  onChange({
                    maxValue: value,
                    product: productKey,
                    maxRadius: metersValue
                  });
              }
            }}></Input>
        </Form.Item>
      </Form>
    </div>
  );
};

// ── Styled components ────────────────────────────────────────────────────────

const RootContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  pointer-events: none;
  user-select: none;
`;

const ControlContainer = styled.div`
  position: absolute;
  bottom: 50px;
  z-index: 20;
  left: 0;
  right: 0;
  width: 100%;
  padding: 10px 50px;
`;

const PlayAndSlider = styled.div`
  display: flex;
  flex-direction: row;
  background: transparent;
  border-radius: 5px;
  padding: 10px 10px;
  gap: 10px;

  pointer-events: auto;
`;

const SliderWrapper = styled.div`
  width: 100%;
`;

const ToolbarContainer = styled.div`
  display: flex;
  flex-direction: row;
  pointer-events: auto;
  justify-content: flex-end;
`;

const ButtonIconCentered = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
`;
